// src/utils/htmlSain.ts
//
// Août 2026 — CORRECTIF DE SÉCURITÉ (constat 08 de l'audit).
//
// Les portails vitrines rendaient `presentation.contenuHtml` — du HTML rédigé
// par le locataire — via `dangerouslySetInnerHTML`, sans aucune désinfection.
// Quiconque pouvait éditer la vitrine injectait du script s'exécutant chez tous
// les visiteurs du portail public, sur l'origine de l'application. Le jeton étant
// conservé en localStorage, le personnel consultant la page y perdait sa session.
//
// Ce module est le seul point d'entrée autorisé pour `dangerouslySetInnerHTML`.

import DOMPurify from 'dompurify';

/**
 * Balises conservées : de quoi rédiger une présentation d'entreprise correcte —
 * titres, paragraphes, listes, mise en forme, liens, tableaux, images.
 * `del` / `ins` s'y ajoutent depuis que le rendu Markdown de l'assistant passe
 * par ici : `~~barré~~` produit un `<del>`, dont la chute faisait perdre le
 * sens du texte.
 * Tout le reste tombe, à commencer par `script`, `iframe`, `object`, `embed`,
 * `form` et les gestionnaires d'événements.
 */
const BALISES_AUTORISEES = [
  'p', 'br', 'hr', 'span', 'div',
  'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
  'strong', 'b', 'em', 'i', 'u', 's', 'del', 'ins', 'sub', 'sup', 'mark', 'small',
  'ul', 'ol', 'li', 'dl', 'dt', 'dd',
  'blockquote', 'pre', 'code',
  'a', 'img', 'figure', 'figcaption',
  'table', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td', 'caption', 'colgroup', 'col',
];

const ATTRIBUTS_AUTORISES = [
  'href', 'title', 'target', 'rel',
  'src', 'alt', 'width', 'height', 'loading',
  'colspan', 'rowspan', 'scope',
  'class', 'style',
];

/**
 * Désinfecte du HTML de provenance non fiable avant affichage.
 *
 * `ALLOWED_URI_REGEXP` bloque `javascript:` et consorts sur les `href`/`src`,
 * qui restent exécutables même sans balise `script`.
 */
export function htmlSain(brut?: string | null): string {
  if (!brut) return '';
  return DOMPurify.sanitize(brut, {
    ALLOWED_TAGS: BALISES_AUTORISEES,
    ALLOWED_ATTR: ATTRIBUTS_AUTORISES,
    ALLOWED_URI_REGEXP: /^(?:https?:|mailto:|tel:|#|\/)/i,
    FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'link', 'meta', 'base'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'formaction', 'srcdoc'],
    // Empêche l'échappement du conteneur par du HTML malformé.
    KEEP_CONTENT: true,
  });
}

/**
 * Prêt à passer à `dangerouslySetInnerHTML` :
 *
 *     <div dangerouslySetInnerHTML={htmlSainProp(vitrine.contenuHtml)} />
 */
export function htmlSainProp(brut?: string | null): { __html: string } {
  return { __html: htmlSain(brut) };
}
